import { Field, Input, RadioGroup } from "@headlessui/react";
import { ApiError as RestApiError } from "@lobby/api-client";
import { useTranslate } from "@lobby/ocb-intl";
import * as Sentry from "@sentry/react";
import { clsx } from "clsx";
import { useMemo, useRef, useState } from "react";

import { PaymentRest, Player } from "../../../../../entities";
import { APIError, Button, Icon, Spinner, emitter, getDocumentLocale } from "../../../../../shared";
import { NoDataPlaceholder, ProfileSection } from "../../components";

import { PaymentMethodCard } from "./components.rest";
import { RestDepositExtraFields, type RestExtraField } from "./deposit-extra-fields";
import {
  getDepositMethodsForCurrency,
  getMethodCommission,
  getMethodLimitsForCard,
  resolveDepositCurrency,
  resolveDepositPaymentType,
  type RestPaymentMethod,
  type RestPaymentMethodsData,
} from "./deposit-rest.helpers";

import type { FormEvent } from "react";

interface DepositResponseInstructions {
  qrImage: string;
  paymentCode: string;
}

export function DepositTabRest() {
  const { $t } = useTranslate();
  const { data, isLoading, isFetching } = PaymentRest.useMethodList("deposit");

  return (
    <Sentry.ErrorBoundary fallback={(error) => <pre>{String(error)}</pre>}>
      <ProfileSection
        isLoading={isLoading || isFetching}
        data={data?.methods}
        placeholder={
          <NoDataPlaceholder
            topText={$t({
              defaultMessage: "Currently, there are no available methods for depositing funds.",
            })}
            bottomText={$t({
              defaultMessage: "Please contact the support chat to deposit funds.",
            })}
            icon={<Icon name="creditCard" className="text-[6.25rem]" />}
          />
        }
      >
        {(validData) => <DepositTabRestView methodsData={data!} methods={validData} />}
      </ProfileSection>
    </Sentry.ErrorBoundary>
  );
}

function DepositTabRestView({
  methodsData,
  methods,
}: {
  methodsData: RestPaymentMethodsData;
  methods: RestPaymentMethod[];
}) {
  const { $t } = useTranslate();
  const { data: player } = Player.usePlayer();

  const selectedCurrency = useMemo(
    () => resolveDepositCurrency(methodsData.currencies, player?.currency),
    [methodsData.currencies, player?.currency],
  );

  const currencyCode = selectedCurrency?.code ?? "";
  const availableMethods = useMemo(
    () => getDepositMethodsForCurrency(methods, currencyCode),
    [methods, currencyCode],
  );

  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);

  const selectedMethod = useMemo(
    () =>
      availableMethods.find((method) => method.id === selectedMethodId) ??
      availableMethods[0] ??
      null,
    [availableMethods, selectedMethodId],
  );

  if (!selectedMethod || !currencyCode) {
    return null;
  }

  return (
    <div className="gutter-stable relative min-h-0 py-1 mobile-only:pb-5 lg:h-full lg:max-h-full lg:overflow-y-auto">
      <ol className="list-outside list-decimal space-y-10 ps-5">
        <li>
          {$t({ defaultMessage: "Choose one of the payment methods" })}
          <RadioGroup
            value={selectedMethod.id}
            onChange={setSelectedMethodId}
            name="payment-method"
            className="mt-2.5"
          >
            <ul className="flex flex-wrap gap-2.5">
              {availableMethods.map((method) => {
                const limits = getMethodLimitsForCard(method, currencyCode);
                const commission = getMethodCommission(method, currencyCode);

                return (
                  <li key={method.id}>
                    <PaymentMethodCard
                      id={method.id}
                      name={method.name}
                      logoUrl={method.img}
                      commission={commission}
                      limits={limits}
                    />
                  </li>
                );
              })}
            </ul>
          </RadioGroup>
        </li>
        <RestDepositPlan
          selectedMethod={selectedMethod}
          selectedCurrency={currencyCode}
          defaultAmount={selectedCurrency?.defaultValue}
        />
      </ol>
    </div>
  );
}

function RestDepositPlan({
  selectedMethod,
  selectedCurrency,
  defaultAmount: currencyDefaultAmount,
}: {
  selectedMethod: RestPaymentMethod;
  selectedCurrency: string;
  defaultAmount?: string | null;
}) {
  const { $t } = useTranslate();
  const [validationError, setValidationError] = useState("");
  const [qrInstructions, setQrInstructions] = useState<DepositResponseInstructions | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { data: player } = Player.usePlayer();
  const { mutate: deposit, isPending } = PaymentRest.useDeposit();

  const methodCurrency = selectedMethod.currencies[selectedCurrency];
  const isQrFlow = selectedMethod.executionStyle === "qr";
  const minAmount = Number(methodCurrency?.limits?.min ?? "0");
  const maxAmount = Number(methodCurrency?.limits?.max ?? "0");
  const defaultAmount = Number(currencyDefaultAmount ?? methodCurrency?.limits?.min ?? "0");
  const extraFieldsConfig = useMemo(
    () =>
      (Array.isArray(selectedMethod.extraFields)
        ? selectedMethod.extraFields
        : []) as RestExtraField[],
    [selectedMethod.extraFields],
  );
  const hasExtraFields = extraFieldsConfig.length > 0;
  const depositFormId = `rest-deposit-form-${selectedMethod.id}`;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!formRef.current || !selectedCurrency) {
      return;
    }

    setValidationError("");
    setQrInstructions(null);

    const formData = new FormData(formRef.current);
    const amount = String(formData.get("deposit-amount") ?? "");

    const extraFields: Record<string, string> = {};
    for (const field of extraFieldsConfig) {
      const value = formData.get(field.code);
      if (value === null || String(value).trim() === "") {
        setValidationError($t({ defaultMessage: "Please fill in all required payment details." }));
        return;
      }
      extraFields[field.code] = String(value);
    }

    deposit(
      {
        methodId: selectedMethod.id,
        type: resolveDepositPaymentType(selectedMethod),
        currency: selectedCurrency,
        language: getDocumentLocale(),
        ...(isQrFlow ? {} : { amount }),
        extraFields,
      },
      {
        onSuccess: (result) => {
          if (result.executionStyle === "url" && result.url) {
            window.location.href = result.url;
            return;
          }

          if (result.executionStyle === "qr" && result.qr) {
            setQrInstructions({
              qrImage: result.qr.qrValue ?? "",
              paymentCode: result.qr.textValue,
            });
          }
        },
        onError: (error) => {
          const message =
            error instanceof RestApiError
              ? error.text
              : error instanceof Error
                ? error.message
                : "Deposit request failed";
          emitter.emit("ERROR", {
            error: new APIError(
              message,
              error instanceof RestApiError ? { code: error.code } : undefined,
            ),
          });
          setValidationError(message);
        },
      },
    );
  }

  const depositButton = (
    <Button
      variant="success"
      type="submit"
      form={depositFormId}
      className="mobile-only:mx-auto mobile-only:w-fit"
    >
      {$t({ defaultMessage: "Deposit" })}
    </Button>
  );

  return (
    <>
      <form
        key={selectedMethod.id}
        id={depositFormId}
        className="hidden"
        onSubmit={handleSubmit}
        ref={formRef}
      />

      <li className="text-14">
        {!isQrFlow && (
          <>
            {$t({ defaultMessage: "Enter the amount you want to deposit" })}
            <Field disabled={!selectedMethod || !selectedCurrency}>
              <div className="billing-information-block mt-2.5">
                <label className="!mb-0">{$t({ defaultMessage: "Amount" })}</label>
                <div className="flex mobile-only:flex-col gap-2.5 lg:items-center">
                  <div className="min-w-0 flex-1">
                    <Input
                      className="input mt-1 w-full appearance-none data-[disabled]:opacity-50"
                      placeholder={$t({ defaultMessage: "Enter the amount" })}
                      name="deposit-amount"
                      form={depositFormId}
                      type="number"
                      inputMode="decimal"
                      min={minAmount > 0 ? minAmount : 0}
                      max={maxAmount > 0 ? maxAmount : undefined}
                      step={0.01}
                      defaultValue={defaultAmount > 0 ? defaultAmount : ""}
                      required
                    />
                  </div>
                  {!hasExtraFields && <div className="shrink-0">{depositButton}</div>}
                </div>
              </div>
            </Field>
          </>
        )}
        {isQrFlow && (
          <div style={{ color: "var(--text-secondary-color)" }} className="mt-2 text-12">
            {$t({ defaultMessage: "Scan the QR code to complete your deposit." })}
          </div>
        )}
        {isQrFlow && !hasExtraFields && <div className="mt-2.5">{depositButton}</div>}
        {!hasExtraFields && (
          <>
            {validationError && (
              <p className="text-center text-alizarin-crimson leading-none">{validationError}</p>
            )}
            <div style={{ color: "var(--text-secondary-color)" }} className="mt-2 text-12">
              {isQrFlow
                ? $t({ defaultMessage: "Scan the QR code to complete your deposit." })
                : $t({
                    defaultMessage:
                      "After clicking 'Deposit', you will be redirected to the payment page.",
                  })}
            </div>
          </>
        )}
      </li>

      {hasExtraFields && (
        <li className="text-14">
          {$t({ defaultMessage: "Fill in payment details" })}
          <RestDepositExtraFields
            fields={extraFieldsConfig}
            player={player}
            formId={depositFormId}
          />
          <div className="mt-10">{depositButton}</div>
          {validationError && (
            <p className="text-center text-alizarin-crimson leading-none">{validationError}</p>
          )}
          <div style={{ color: "var(--text-secondary-color)" }} className="mt-2 text-12">
            {isQrFlow
              ? $t({ defaultMessage: "Scan the QR code to complete your deposit." })
              : $t({
                  defaultMessage:
                    "After clicking 'Deposit', you will be redirected to the payment page.",
                })}
          </div>
        </li>
      )}
      {qrInstructions && (
        <RestDepositQrInstructions
          qrcode={qrInstructions.qrImage}
          paymentCode={qrInstructions.paymentCode}
        />
      )}
      {isPending && (
        <div className="fixed mobile-only:absolute inset-0 flex-center backdrop-blur-sm [margin:0!important]">
          <Spinner />
        </div>
      )}
    </>
  );
}

function RestDepositQrInstructions({
  qrcode,
  paymentCode,
}: {
  qrcode: string;
  paymentCode: string;
}) {
  const { $t } = useTranslate();
  const [copied, setCopied] = useState(false);
  const timeoutID = useRef<ReturnType<typeof setTimeout> | null>(null);
  const qrImageSrc = qrcode.startsWith("data:image")
    ? qrcode
    : /^[A-Za-z0-9+/=]+$/.test(qrcode)
      ? `data:image/jpeg;base64,${qrcode}`
      : "";

  const handleCopy = () => {
    navigator.clipboard.writeText(paymentCode);
    setCopied(true);

    if (timeoutID.current) {
      clearTimeout(timeoutID.current);
    }
    timeoutID.current = setTimeout(setCopied, 2000, false);
  };

  return (
    <div>
      <div className="mb-3 text-center lg:text-lg">
        {$t({ defaultMessage: "Choose a payment method" })}
      </div>
      <div className="flex gap-3 text-xs lg:text-sm">
        <div className="max-w-40">
          <div>{$t({ defaultMessage: "Scan the QR code:" })}</div>
          <div className="mt-1">
            {qrImageSrc && <img src={qrImageSrc} alt="QR Code" className="size-40 min-w-40" />}
          </div>
        </div>
        <div className="billing-information-block min-w-0 flex-1">
          <label className="!mb-0">{$t({ defaultMessage: "Copy and paste ID:" })}</label>
          <Input
            type="text"
            value={paymentCode}
            readOnly
            className="input mt-1 w-full outline-[var(--accent-color)]"
          />
          <div className="relative w-fit">
            <Button variant="secondary" onClick={handleCopy} className="my-3">
              <span>{$t({ defaultMessage: "Copy ID" })}</span>
              <span
                className={clsx(
                  "border-[var(--accent-color)] border-1 -top-1 absolute left-1/2 bg-black -translate-x-1/2 -translate-y-1 bg-gray-800 text-xs rounded-rounded px-2 py-1",
                  copied ? "opacity-100" : "opacity-0 transition-opacity duration-300",
                )}
              >
                {$t({ defaultMessage: "Copied!" })}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
